import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/api';

/**
 * Custom hook for fetching and managing events/activity logs
 * Used by V2 Events page for displaying user activity
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoFetch - Whether to fetch on mount (default: true)
 * @param {number} options.autoRefreshInterval - Auto-refresh interval in ms (default: 60000)
 * @returns {Object} Events data and utility functions
 */
export function useEvents(options = {}) {
  const {
    autoFetch = true,
    autoRefreshInterval = 60000, // 60 seconds
  } = options;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filters, setFilters] = useState({
    category: null,
    unreadOnly: false,
  });

  // Store pagination key for load more functionality
  const nextKeyRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  /**
   * Fetch events from API
   * @param {Object} params - Fetch parameters
   * @param {boolean} params.reset - Whether to reset pagination (default: true)
   * @param {boolean} params.silent - Whether to suppress loading state (for auto-refresh)
   */
  const fetchEvents = useCallback(async ({ reset = true, silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);

      const params = new URLSearchParams({
        limit: 20,
      });

      // Add filters
      if (filters.category) {
        params.append('category', filters.category);
      }
      if (filters.unreadOnly) {
        params.append('unreadOnly', 'true');
      }

      // Add pagination key if loading more
      if (!reset && nextKeyRef.current) {
        params.append('lastKey', nextKeyRef.current);
      }

      const response = await axios.get(`${API_BASE_URL}/logs?${params.toString()}`, {
        withCredentials: true,
      });

      const { logs, hasMore: more, nextKey } = response.data;

      if (reset) {
        setEvents(logs || []);
      } else {
        setEvents(prev => [...prev, ...(logs || [])]);
      }

      setHasMore(more);
      nextKeyRef.current = nextKey || null;
      setLoading(false);

      return response.data;
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load events');
      setLoading(false);
      throw err;
    }
  }, [filters]);

  /**
   * Fetch unread count for badge display
   */
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/logs/unread-count`, {
        withCredentials: true,
      });
      setUnreadCount(response.data.count || 0);
    } catch (err) {
      console.error('Error fetching unread count:', err);
      // Don't set error state for unread count failures
    }
  }, []);

  /**
   * Load more events (pagination)
   */
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchEvents({ reset: false });
  }, [hasMore, loading, fetchEvents]);

  /**
   * Manual refresh - reset pagination and fetch fresh data
   */
  const refresh = useCallback(async () => {
    nextKeyRef.current = null;
    await Promise.all([
      fetchEvents({ reset: true }),
      fetchUnreadCount(),
    ]);
  }, [fetchEvents, fetchUnreadCount]);

  /**
   * Mark specific events as read
   * @param {Array} logIds - Array of { logId, timestamp } objects
   */
  const markAsRead = useCallback(async (logIds) => {
    try {
      await axios.patch(`${API_BASE_URL}/logs/mark-read`, {
        logIds,
      }, {
        withCredentials: true,
      });

      // Update local state to mark events as read
      setEvents(prev => prev.map(event => {
        const found = logIds.find(item => item.logId === event.logId);
        if (found) {
          return { ...event, read: true };
        }
        return event;
      }));

      // Refresh unread count
      await fetchUnreadCount();

      // Dispatch event to notify other components (e.g., sidebar) to refresh their count
      window.dispatchEvent(new CustomEvent('unread-count-changed'));
    } catch (err) {
      console.error('Error marking events as read:', err);
      throw err;
    }
  }, [fetchUnreadCount]);

  /**
   * Mark ALL events as read (not just visible ones)
   * Calls the backend endpoint that marks all unread events for the user
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await axios.patch(`${API_BASE_URL}/logs/mark-all-read`, {}, {
        withCredentials: true,
      });

      // Update local state to mark all currently loaded events as read
      setEvents(prev => prev.map(event => ({ ...event, read: true })));

      // Refresh unread count
      await fetchUnreadCount();

      // Dispatch event to notify other components (e.g., sidebar) to refresh their count
      window.dispatchEvent(new CustomEvent('unread-count-changed'));
    } catch (err) {
      console.error('Error marking all events as read:', err);
      throw err;
    }
  }, [fetchUnreadCount]);

  /**
   * Update filters - this will trigger a new fetch
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
    }));
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({
      category: null,
      unreadOnly: false,
    });
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    if (autoFetch && isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      Promise.all([
        fetchEvents({ reset: true }),
        fetchUnreadCount(),
      ]);
    }
  }, [autoFetch, fetchEvents, fetchUnreadCount]);

  // Refetch when filters change (but not on initial load)
  useEffect(() => {
    if (!isInitialLoadRef.current) {
      nextKeyRef.current = null;
      fetchEvents({ reset: true });
    }
  }, [filters, fetchEvents]);

  // Auto-refresh interval
  useEffect(() => {
    if (!autoRefreshInterval) return;

    const interval = setInterval(() => {
      fetchEvents({ reset: true, silent: true });
      fetchUnreadCount();
    }, autoRefreshInterval);

    return () => clearInterval(interval);
  }, [autoRefreshInterval, fetchEvents, fetchUnreadCount]);

  return {
    events,
    loading,
    error,
    hasMore,
    unreadCount,
    filters,
    setFilters: updateFilters,
    loadMore,
    refresh,
    markAsRead,
    markAllAsRead,
    clearFilters,
  };
}

export default useEvents;
